import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, CheckCircle, X, Plus, FileText, Image, Video } from "lucide-react";
import MediaPreview from "@/components/common-components/cards/MediaPreview";
const CreativeUpload = ({
	files = [],
	onFilesChange,
	label = "Creatives",
	accept = "image/*,video/*",
	id = "file-upload",
	description = "Supported formats: JPG, PNG, GIF, MP4",
	showPreviews = true,
	multiple = true,
	labelSuffix = "(Optional)",
}) => {
	const handleFileUpload = (e) => {
		// console.log('File input changed:', e.target.files);
		const allowed = Array.from(e.target.files || []).filter((file) => {
			const type = (file.type || '').toLowerCase();
			const name = (file.name || '').toLowerCase();
			const okMime = type.startsWith('image/') || type.startsWith('video/');
			const okExt = /\.(jpe?g|png|gif|webp|mp4|mov|avi|mpeg|mpg)$/i.test(name);
			return okMime || okExt;
		});
		const rejected = (e.target.files?.length || 0) - allowed.length;
		if (rejected > 0) {
			alert('Only image/video creatives are allowed (JPG, PNG, GIF, WebP, MP4, MOV, AVI).');
		}
		if (allowed.length > 0) {
			onFilesChange([...files, ...allowed]);
		}
		// Reset the input value to allow re-uploading the same file
		e.target.value = '';
	};

	const removeFile = (index) => {
		const updatedFiles = [...files];
		updatedFiles.splice(index, 1);
		onFilesChange(updatedFiles);
	};

	const formatFileSize = (bytes) => {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
	};

	return (
		<div className="w-full">
			<Label htmlFor={id} className="font-medium block mb-2">
				{label} <span className="text-sm font-normal text-gray-500">{labelSuffix}</span>
			</Label>
			<div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
				<input
					id={id}
					type="file"
					accept={accept}
					onChange={handleFileUpload}
					className="hidden"
					multiple={multiple}
				/>

				<div className="text-center mb-4">
					<Button 
						type="button"
						variant="outline" 
						onClick={() => {
							// console.log('Upload button clicked');
							document.getElementById(id).click();
						}}
						className="mx-auto mb-2"
					>
						<Upload className="mr-2 h-4 w-4" /> Upload Files
					</Button>
					<p className="text-sm text-gray-500">{description}</p>
				</div>

				{files.length > 0 && (
					<div className="mt-4">
						<div className="flex justify-between items-center mb-2">
							<h3 className="font-medium">Uploaded Files ({files.length})</h3>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => document.getElementById(id).click()}
							>
								<Plus className="h-4 w-4 mr-1" /> Add More
							</Button>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{files.map((file, index) => (
								<div key={index} className="border rounded-md p-3 bg-gray-50 flex flex-col">
									<div className="flex justify-between items-start mb-2">
										<div className="flex items-center flex-1 min-w-0">
											{file.type && file.type.startsWith('image/') ? (
												<Image className="text-blue-500 mr-2 h-4 w-4 flex-shrink-0" />
											) : file.type && file.type.startsWith('video/') ? (
												<Video className="text-purple-500 mr-2 h-4 w-4 flex-shrink-0" />
											) : (
												<FileText className="text-orange-500 mr-2 h-4 w-4 flex-shrink-0" />
											)}
											<div className="flex-1 min-w-0">
												<span className="text-sm font-medium truncate block" title={file.name}>
													{file.name}
												</span>
												{file.size && (
													<span className="text-xs text-gray-500">
														{formatFileSize(file.size)}
													</span>
												)}
											</div>
										</div>
										<Button
											variant="ghost"
											size="sm"
											className="p-1 h-auto ml-2 flex-shrink-0"
											onClick={() => removeFile(index)}
										>
											<X className="h-4 w-4 text-gray-500" />
										</Button>
									</div>

									{showPreviews && file.type && (file.type.startsWith('image/') || file.type.startsWith('video/')) && file instanceof File && (
										<div className="mt-2 flex justify-center">
											<MediaPreview file={file} />
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default CreativeUpload;
